import {
  Dropdown as NextUIDropdown,
  DropdownMenu as NextUIDropdownMenu,
  DropdownItem as NextUIDropdownMenuItem,
  DropdownItemProps as NextUIDropdownMenuItemProps,
  DropdownMenuProps as NextUIDropdownMenuProps,
  DropdownProps as NextUIDropdownProps,
  DropdownSection as NextUIDropdownSection,
  DropdownSectionProps as NextUIDropdownSectionProps,
  DropdownTrigger as NextUIDropdownTrigger,
} from "@heroui/react";
import clsx from "clsx";
import { TRACK_EVENTS } from "core/consts";
import { Button } from "ds/ui";
import { ReactElement } from "react";
import { useTracking } from "react-tracking";

export type DropdownProps = {
  DropdownTrigger: ReactElement<typeof Button>;
  ariaDescribedBy?: string;
  ariaLabel: string;
  sections: DropdownSectionProps[];
} & Pick<
  NextUIDropdownProps,
  "closeOnSelect" | "placement" | "onOpenChange" | "isOpen"
> &
  Pick<
    NextUIDropdownMenuProps,
    "topContent" | "selectionMode" | "disallowEmptySelection"
  >;

export type DropdownSectionProps = Omit<
  NextUIDropdownSectionProps,
  "children"
> & {
  items: DropdownMenuItemProps[];
};

type Color = Extract<
  NextUIDropdownMenuItemProps["color"],
  "success" | "primary" | "danger"
>;

export type DropdownMenuItemProps = Omit<
  NextUIDropdownMenuItemProps,
  "variant" | "color"
> & {
  color: Color;
};

export function Dropdown({
  ariaDescribedBy,
  ariaLabel,
  closeOnSelect = true,
  DropdownTrigger,
  isOpen,
  onOpenChange,
  placement = "bottom",
  sections,
  selectionMode = "none",
  topContent,
}: DropdownProps) {
  const { trackEvent } = useTracking();
  const nonEmptySections = sections.filter(({ items }) => items.length > 0);

  return (
    <NextUIDropdown
      closeOnSelect={closeOnSelect}
      isOpen={isOpen}
      placement={placement}
      onOpenChange={onOpenChange}
    >
      <NextUIDropdownTrigger className="aria-expanded:scale-1">
        {DropdownTrigger}
      </NextUIDropdownTrigger>
      <NextUIDropdownMenu
        aria-describedby={ariaDescribedBy}
        aria-label={ariaLabel}
        classNames={{
          base: "w-full max-h-[80vh] desktop:h-full overflow-y-auto outline-none max-w-[350px]",
        }}
        disallowEmptySelection
        selectionMode={selectionMode}
        topContent={topContent}
      >
        {nonEmptySections.map(({ items, showDivider, title }, sectionIndex) => {
          return (
            <NextUIDropdownSection
              key={sectionIndex}
              classNames={{ group: "flex flex-col gap-0.5" }}
              showDivider={showDivider}
              title={title}
            >
              {items.map(({ key, ...item }: NextUIDropdownMenuItemProps) => {
                return (
                  <NextUIDropdownMenuItem
                    className={clsx(
                      "[&>svg]:!w-4",
                      "group !outline-none-v3 rounded-lg !bg-white p-2 data-[focus=true]:!ring-1 data-[hover=true]:!ring-1",
                      "data-[focus=true]:!outline data-[focus=true]:!outline-1 data-[focus=true]:!outline-offset-1",
                      "data-[hover=true]:!outline data-[hover=true]:!outline-1 data-[hover=true]:!outline-offset-1",
                      {
                        "data-[focus=true]:!bg-primary-light data-[hover=true]:!bg-primary-light data-[focus=true]:!text-primary-light-foreground data-[hover=true]:!text-primary-light-foreground data-[focus=true]:ring-primary data-[hover=true]:ring-primary":
                          item.color === "primary",
                        "data-[focus=true]:!bg-secondary-light data-[hover=true]:!bg-secondary-light data-[focus=true]:!text-secondary-light-foreground data-[hover=true]:!text-secondary-light-foreground data-[focus=true]:ring-secondary-dark data-[hover=true]:ring-secondary-dark":
                          item.color === "secondary",
                        "data-[focus=true]:!bg-warning-light data-[hover=true]:!bg-warning-light data-[focus=true]:!text-warning-light-foreground data-[hover=true]:!text-warning-light-foreground data-[focus=true]:ring-warning data-[hover=true]:ring-warning":
                          item.color === "warning",
                        "text-danger data-[focus=true]:!bg-danger-light data-[hover=true]:!bg-danger-light data-[focus=true]:!text-danger-dark data-[hover=true]:!text-danger-dark data-[focus=true]:ring-danger data-[hover=true]:ring-danger":
                          item.color === "danger",
                        "text-success data-[focus=true]:!bg-success-light data-[hover=true]:!bg-success-light data-[focus=true]:!text-success-dark data-[hover=true]:!text-success-dark data-[focus=true]:ring-success data-[hover=true]:ring-success":
                          item.color === "success",
                      },
                      item.className,
                    )}
                    endContent={item.endContent}
                    startContent={item.startContent}
                    {...item}
                    key={key}
                    data-key={key}
                    data-testid={`dropdown-item-${key}`}
                    onPress={(e) => {
                      trackEvent({
                        item: key,
                        name: TRACK_EVENTS.DROPDOWN_ITEM_CLICKED,
                      });
                      // Run synchronously so dialogs/modals open before the dropdown closes (avoids detached element in tests and improves UX)
                      item.onPress?.(e);
                    }}
                  >
                    {item.title}
                  </NextUIDropdownMenuItem>
                );
              })}
            </NextUIDropdownSection>
          );
        })}
      </NextUIDropdownMenu>
    </NextUIDropdown>
  );
}

Dropdown.displayName = "RecareUI.Dropdown";
